import React, { useState, useEffect } from "react";
import axios from "axios";
import RequestResetForm from "./RequestResetForm";
import VerifyResetForm from "./VerifyResetForm";
import { Form, ErrorMessage, SuccessMessage } from "./Login.styles.js";

const ForgotPasswordForm = ({ switchToLogin }) => {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setResendEnabled(true);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRequestReset = async (email) => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/auth/forgot-password`,
        { email }
      );

      // Handle successful email sent (200 OK)
      if (response.status === 200) {
        setEmail(email);
        setSuccess(
          response.data.message ||
            "Password reset link has been sent to your email."
        );
        setStep("verify");
        setCountdown(60);
        setResendEnabled(false);
      }
      // Handle account pending approval (403)
      else if (response.status === 403) {
        setError(response.data.message || "Your account is pending approval.");
      }
    } catch (err) {
      // Handle user doesn't exist (404)
      if (err.response?.status === 404) {
        setError(
          err.response.data.message ||
            "No account found with this email address."
        );
      }
      // Handle invalid email format (400)
      else if (err.response?.status === 400) {
        setError(
          err.response.data.message || "Please enter a valid email address."
        );
      }
      // Handle email sending failure (500)
      else if (err.response?.status === 500) {
        setError(
          err.response.data.message ||
            "Failed to send reset email. Please try again."
        );
      }
      // Handle network errors or other issues
      else if (!err.response) {
        setError("Network error. Please check your connection and try again.");
      }
      // Fallback for unexpected responses
      else {
        setError(err.response?.data?.message || "Failed to process request");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!resendEnabled) return;

    setError("");
    setLoading(true);
    setResendEnabled(false);
    setCountdown(60);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/auth/forgot-password`,
        { email }
      );
      setSuccess("Reset email resent. Please check your inbox.");
    } catch (err) {
      setSuccess(
        "If an account exists with this email, you will receive a password reset link."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (step) {
      case "request":
        return (
          <RequestResetForm
            onRequestReset={handleRequestReset}
            loading={loading}
            switchToLogin={switchToLogin}
          />
        );
      case "verify":
        return (
          <VerifyResetForm
            email={email}
            countdown={countdown}
            resendEnabled={resendEnabled}
            onResendEmail={handleResendEmail}
            switchToLogin={switchToLogin}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Form>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      {renderForm()}
    </Form>
  );
};

export default ForgotPasswordForm;
